import React, { useContext, useEffect, useMemo, useState } from "react";
import styles from "./Affiliates.module.css";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import { useQuery, useMutation, useApolloClient } from "@apollo/client";
import { toast } from "react-toastify";
import { 
  GET_ALL_AFFILIATES,
  GET_AFFILIATES, 
  UPDATE_AFFILIATE,
  GET_AFFILIATE_COUNT,
  GET_AFFILIATE_USERS,
  GET_AFFILIATE_PAYOUTS,
  GET_REFERRED_AFFILIATES,
  GET_USERS_BY_AFFILIATE_ID,
  GET_AFFILIATE_BY_ID_DIRECT_INDIRECT,
  GET_AFFILIATE_ROW_BY_PK,
  GET_AFFILIATE_BUY_SUBSCRIPTION_COMMISSIONS,
} from "./api/AffiliateQuery";
import { createAffiliateViaNodeApi } from "./api/createAffiliateApi";
import { editAffiliateViaNodeApi } from "./api/editAffiliateApi";
import { config } from "../../config/config";
import generateDynamicLink from "../../config/branchIo";
import { useShortReferralLink } from "../../config/useShortReferralLink";
import { includeSignupRewards } from "./affiliateEarnings";
import { roles } from "../../helper";
import { globalContext } from "../../Provider/AppProvider";
import { GET_SALES_REPS, GET_USERS_LIST } from "../Users/api/UsersQuery";
import AffiliatesAndSalesRepsDropdown, { CombinedItem } from "../../components/shared-components/dropdown/AffiliatesAndSalesRepsDropdown";

type AffiliateMetrics = {
  signupReferralEarnings?: number;
  totalAmount: number;
  directPayout: number;
  indirectPayout: number;
  sellTotalAmount: number;
  sellDirectPayout: number;
  sellIndirectPayout: number;
  buyTotalAmount: number;
  buyDirectPayout: number;
  buyIndirectPayout: number;
};

const emptyAffiliateMetrics = (): AffiliateMetrics => ({
  totalAmount: 0,
  directPayout: 0,
  indirectPayout: 0,
  sellTotalAmount: 0,
  sellDirectPayout: 0,
  sellIndirectPayout: 0,
  buyTotalAmount: 0,
  buyDirectPayout: 0,
  buyIndirectPayout: 0,
});

const mergeSellAndBuyMetrics = (
  sell: Pick<AffiliateMetrics, "sellTotalAmount" | "sellDirectPayout" | "sellIndirectPayout">,
  buy?: Pick<AffiliateMetrics, "buyTotalAmount" | "buyDirectPayout" | "buyIndirectPayout"> | null,
): AffiliateMetrics => {
  const buyTotalAmount = Number(buy?.buyTotalAmount || 0) || 0;
  const buyDirectPayout = Number(buy?.buyDirectPayout || 0) || 0;
  const buyIndirectPayout = Number(buy?.buyIndirectPayout || 0) || 0;
  const sellTotalAmount = Number(sell.sellTotalAmount || 0) || 0;
  const sellDirectPayout = Number(sell.sellDirectPayout || 0) || 0;
  const sellIndirectPayout = Number(sell.sellIndirectPayout || 0) || 0;
  return {
    sellTotalAmount,
    sellDirectPayout,
    sellIndirectPayout,
    buyTotalAmount,
    buyDirectPayout,
    buyIndirectPayout,
    totalAmount: sellTotalAmount + buyTotalAmount,
    directPayout: sellDirectPayout + buyDirectPayout,
    indirectPayout: sellIndirectPayout + buyIndirectPayout,
  };
};

export default function Affiliates() {
  const apolloClient = useApolloClient();
  const { globalState } = useContext<any>(globalContext);
  const [selected, setSelected] = useState<any>(null);
  const originalReferralLink = selected?.referralLink || selected?.referralUrl || selected?.referral_url || '';
  const { link: shortReferralLink, loading: shortLinkLoading, error: shortLinkError } = useShortReferralLink(originalReferralLink, selected?.referralCode);
  const [isEditing, setIsEditing] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });
  
  const [isAddingAffiliate, setIsAddingAffiliate] = useState(false);
  const [isUpdatingAffiliate, setIsUpdatingAffiliate] = useState(false);
  const [affiliateMetaById, setAffiliateMetaById] = useState<Record<string, any>>({});
  const activeHasuraRole = (localStorage.getItem("x-hasura-user-role") || "").toLowerCase().trim();
  const profileRole = (globalState?.profile?.userRole || "").toLowerCase().trim();
  const effectiveRole = activeHasuraRole || profileRole;
  const canManageAffiliates =
    effectiveRole === roles.ADMIN ||
    effectiveRole === roles.SUB_ADMIN ||
    effectiveRole === roles.MARKETING_USER;
  const isAffiliateAdmin = effectiveRole === roles.AFFILIATER_ADMIN;
  const useLegacyAffiliateQuery = isAffiliateAdmin;
  const isMarketingUser = false;
  const loggedInEmail = (
    globalState?.profile?.mailId ||
    globalState?.profile?.email ||
    globalState?.profile?.u_email_id ||
    ""
  )
    .toLowerCase()
    .trim();
  
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);
  
  const [statusFilter, setStatusFilter] = useState<"All" | "Active" | "Deactive">("All");
  const [typeFilter, setTypeFilter] = useState<"All" | "Direct" | "Indirect" | "Both">("All");
  const [searchText, setSearchText] = useState("%");
  const [editData, setEditData] = useState<any>({});
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<"details" | "users" | "payouts">("details");
  const [showReferralPopup, setShowReferralPopup] = useState(false);
  const [showAddAffiliate, setShowAddAffiliate] = useState(false);
  const [referredAffiliateSearch, setReferredAffiliateSearch] = useState("");
  const [showAffiliatesAndSalesRepsSelector, setShowAffiliatesAndSalesRepsSelector] = useState(false);
  const [selectedAffiliateOrSalesRep, setSelectedAffiliateOrSalesRep] = useState<CombinedItem | null>(null);
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    firstName?: string;
    lastName?: string;
    emailId?: string;
    phoneNumber?: string;
    urlSuffix?: string;
  }>({});
  
  const [newAffiliateData, setNewAffiliateData] = useState({
    firstName: "",
    lastName: "",
    emailId: "",
    phoneNumber: "",
    organization: "",
    aType: "Direct",
    referralCode: "",
    referralLink: "",
    isNonProfitOrganization: false,
    referredBy: null as string | null,
    referredByItem: null as CombinedItem | null,
    urlSuffix: ""
  });
  
  const [affiliatePageOffset, setAffiliatePageOffset] = useState(0);
  const [affiliateTotalCount, setAffiliateTotalCount] = useState(0);
  const [userPageOffset, setUserPageOffset] = useState(0);
  const [payoutPageOffset, setPayoutPageOffset] = useState(0);
  const [userTotalCount, setUserTotalCount] = useState(0);
  const [payoutTotalCount, setPayoutTotalCount] = useState(0);
  const [totalAssignedUsersCount, setTotalAssignedUsersCount] = useState<number>(0);
  const [nodeAffiliateMetrics, setNodeAffiliateMetrics] = useState<
    Map<
      string,
      {
        totalAmount: number;
        directPayout: number;
        indirectPayout: number;
        sellTotalAmount: number;
        sellDirectPayout: number;
        sellIndirectPayout: number;
        buyTotalAmount: number;
        buyDirectPayout: number;
        buyIndirectPayout: number;
      }
    >
  >(new Map());
  const pageSize = 25;
  const limit = 25;

  const {
    data: modernAffiliatesData,
    loading: modernAffiliatesLoading,
    error: modernAffiliatesError,
    refetch: refetchModernAffiliates,
  } = useQuery(GET_AFFILIATES, {
    variables: {
      limit,
      offset: affiliatePageOffset,
      searchText,
      aStatus: statusFilter === "All" ? ["Active", "Deactive"] : [statusFilter],
      aType: typeFilter === "All" ? ["Direct", "Indirect", "Both"] : [typeFilter],
      order_by: { aCreatedAt: "desc" },
    },
    skip: false,
  });

  const {
    data: legacyAffiliatesData,
    loading: legacyAffiliatesLoading,
    refetch: refetchLegacyAffiliates,
  } = useQuery(GET_ALL_AFFILIATES, {
    variables: {
      pageSize: limit,
      pageNumber: affiliatePageOffset,
      order_by: { a_created_at: "desc" },
      search_event: searchText,
    },
    skip: false,
  });

  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [generatedLink, setGeneratedLink] = useState("");

  const {
    data: affiliateCountData,
    refetch: refetchAffiliateCount,
  } = useQuery(GET_AFFILIATE_COUNT, {
    variables: {
      searchText,
      aStatus: statusFilter === "All" ? null : statusFilter,
      aType: typeFilter === "All" ? null : typeFilter,
      referrerId: null,
    },
    onCompleted: (countData) => {
      setAffiliateTotalCount(countData?.GetAffiliatesCount?.totalCount || 0);
    },
    skip: useLegacyAffiliateQuery,
  });

  const { data: salesRepsData } = useQuery(GET_SALES_REPS, {
    fetchPolicy: "network-only",
  });

  const { data: allUsersData } = useQuery(GET_USERS_LIST, {
    variables: {
      pageSize: 5000,
      pageOffset: 0,
      searchText: "%",
      order_by: [{ u_created_at: "desc" }],
    },
    skip: !isMarketingUser,
    fetchPolicy: "network-only",
  });

  const { data: summaryAffiliatesData, error: summaryAffiliatesError, refetch: refetchSummaryAffiliates } = useQuery(GET_AFFILIATES, {
    variables: {
      limit: 50000,
      offset: 0,
      searchText: "%",
      aStatus: ["Active", "Deactive"],
      aType: ["Direct", "Indirect", "Both"],
      order_by: { aCreatedAt: "desc" },
    },
    fetchPolicy: "network-only",
    skip: false,
  });

  const { data: summaryLegacyAffiliatesData, refetch: refetchSummaryLegacyAffiliates } = useQuery(GET_ALL_AFFILIATES, {
    variables: {
      pageSize: 50000,
      pageNumber: 0,
      search_event: "%",
      order_by: { a_created_at: "desc" },
    },
    fetchPolicy: "network-only",
    skip: false,
  });

  const { data: summaryCountData, refetch: refetchSummaryCount } = useQuery(GET_AFFILIATE_COUNT, {
    variables: {
      searchText: "%",
      aStatus: null,
      aType: null,
      referrerId: null,
    },
    fetchPolicy: "network-only",
    skip: useLegacyAffiliateQuery,
  });

  const { data: buySubscriptionCommissionsData } = useQuery(
    GET_AFFILIATE_BUY_SUBSCRIPTION_COMMISSIONS,
    {
      fetchPolicy: "network-only",
      skip: false,
    }
  );

  // Only fall back when the modern native query fails — not when filters return zero rows.
  const shouldUseLegacyAffiliateData =
    useLegacyAffiliateQuery || !!modernAffiliatesError;

  const loading = shouldUseLegacyAffiliateData ? legacyAffiliatesLoading : modernAffiliatesLoading;
  const refetch = shouldUseLegacyAffiliateData ? refetchLegacyAffiliates : refetchModernAffiliates;
  const refetchAffiliateSummaryData = () =>
    Promise.all([
      shouldUseLegacyAffiliateData ? refetchLegacyAffiliates() : refetchAffiliateCount(),
      shouldUseLegacyAffiliateData ? refetchSummaryLegacyAffiliates() : refetchSummaryAffiliates(),
      shouldUseLegacyAffiliateData ? Promise.resolve() : refetchSummaryCount(),
      loadAffiliateEarnedMetrics(),
      shouldUseLegacyAffiliateData ? Promise.resolve() : refetchSummaryLegacyAffiliates(),
    ]);

  const { data: referredAffiliatesData } = useQuery(GET_REFERRED_AFFILIATES, {
    fetchPolicy: "network-only",
    skip: isMarketingUser,
  });

  const nodeApiBaseUrl = (() => {
    const configured = String(
      process.env.REACT_APP_NODE_API_URL || config.node_api_url || "",
    ).trim();
    // config fallback is often a Hasura URL; earnedMetrics lives on Sell node-backend.
    const looksLikeHasura = /hasura/i.test(configured);
    if (looksLikeHasura || !configured) {
      return window.location.hostname === "localhost" ? "http://localhost:4000" : configured.replace(/\/+$/, "");
    }
    return configured.replace(/\/+$/, "");
  })();
  const getNodeAuthHeaders = () => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes("idToken")) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : "";
    const hasuraRole = localStorage.getItem("x-hasura-user-role") || "";
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers["x-hasura-role"] = hasuraRole;
    return headers;
  };

  const validateEditEmail = (email: string): boolean => {
    if (!email || email.trim() === "") return false;
    const emailRegex = /^[^\s@]+@([^\s@]+\.)+[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateEditPhoneNumber = (phone: string): boolean => {
    if (!phone || phone.trim() === "") return false;
    const cleanPhone = phone.replace(/\D/g, '');
    return cleanPhone.length >= 10 && cleanPhone.length <= 15;
  };

  const loadAffiliateEarnedMetrics = async () => {
    if (!nodeApiBaseUrl) return;
    try {
      const response = await fetch(`${nodeApiBaseUrl}/api/affiliate/earnedMetrics`, {
        method: "POST",
        headers: getNodeAuthHeaders(),
        body: JSON.stringify({ input: {} }),
      });
      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) return;

      const map = new Map<
        string,
        {
          totalAmount: number;
          directPayout: number;
          indirectPayout: number;
          sellTotalAmount: number;
          sellDirectPayout: number;
          sellIndirectPayout: number;
          buyTotalAmount: number;
          buyDirectPayout: number;
          buyIndirectPayout: number;
        }
      >();
      (responseData?.affiliates || []).forEach((affiliate: any) => {
        const sellTotalAmount = Number(affiliate?.sellTotalAmount ?? 0) || 0;
        const sellDirectPayout = Number(affiliate?.sellDirectPayout ?? 0) || 0;
        const sellIndirectPayout = Number(affiliate?.sellIndirectPayout ?? 0) || 0;
        const buyTotalAmount = Number(affiliate?.buyTotalAmount ?? 0) || 0;
        const buyDirectPayout = Number(affiliate?.buyDirectPayout ?? 0) || 0;
        const buyIndirectPayout = Number(affiliate?.buyIndirectPayout ?? 0) || 0;
        const metrics = {
          totalAmount:
            Number(affiliate?.totalAmount ?? sellTotalAmount + buyTotalAmount) ||
            sellTotalAmount + buyTotalAmount,
          directPayout:
            Number(affiliate?.directPayout ?? sellDirectPayout + buyDirectPayout) ||
            sellDirectPayout + buyDirectPayout,
          indirectPayout:
            Number(affiliate?.indirectPayout ?? sellIndirectPayout + buyIndirectPayout) ||
            sellIndirectPayout + buyIndirectPayout,
          sellTotalAmount,
          sellDirectPayout,
          sellIndirectPayout,
          buyTotalAmount,
          buyDirectPayout,
          buyIndirectPayout,
        };
        if (affiliate?.aId) map.set(`a:${affiliate.aId}`, metrics);
        if (affiliate?.uId) map.set(`u:${affiliate.uId}`, metrics);
      });
      setNodeAffiliateMetrics(map);
    } catch (_error) {}
  };

  const loadAssignedUsersCount = async () => {
    try {
      const response = await fetch(`${nodeApiBaseUrl}/api/affiliate/assignedUsersCount`, {
        method: "POST",
        headers: getNodeAuthHeaders(),
        body: JSON.stringify({ input: {} }),
      });
      const responseData = await response.json().catch(() => ({}));
      if (response.ok) {
        setTotalAssignedUsersCount(responseData?.totalUsers || 0);
      }
    } catch (_error) {}
  };

  const {
    data: usersData, 
    loading: usersLoading, 
    refetch: refetchUsers 
  } = useQuery(GET_AFFILIATE_USERS, {
    variables: {
      affiliateId: selected?.aId,
      limit: pageSize,
      offset: userPageOffset,
    },
    skip: !selected?.aId || isMarketingUser,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      setUserTotalCount(data?.getAffiliateUsers?.aggregate?.count || 0);
    }
  });

  const {
    data: assignedUsersData,
    loading: assignedUsersLoading,
    refetch: refetchAssignedUsers
  } = useQuery(GET_USERS_BY_AFFILIATE_ID, {
    variables: {
      affiliateIds: [selected?.aId, selected?.uId, selected?.user?.uId, selected?.user?.id].filter(Boolean),
      affiliateTextIds: [selected?.aId, selected?.uId, selected?.user?.uId, selected?.user?.id, selected?.referralCode, selected?.referral_code].filter(Boolean).map(String),
    },
    skip: !selected?.aId && !selected?.uId,
    fetchPolicy: "network-only",
  });
  const assignedUsers: any[] = assignedUsersData?.user || [];
  useEffect(() => {
    if (!isMarketingUser) return;
    setUserTotalCount(assignedUsers.length);
  }, [isMarketingUser, assignedUsers.length]);

  const { 
    data: payoutsData, 
    loading: payoutsLoading, 
    refetch: refetchPayouts 
  } = useQuery(GET_AFFILIATE_PAYOUTS, {
    variables: {
      affiliateId: selected?.aId,
      limit: pageSize,
      offset: payoutPageOffset,
    },
    skip: !selected?.aId || activeTab !== "payouts",
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      setPayoutTotalCount(data?.getAffiliatePayouts?.aggregate?.count || 0);
    }
  });

  const [updateAffiliate] = useMutation(UPDATE_AFFILIATE);

  const resolveNonProfitFundFlag = (isNonProfit: boolean, organization?: string | null) =>
    Boolean(isNonProfit && organization?.trim());

  const resolveAffiliateUser = (user: any) =>
    Array.isArray(user) ? user[0] : user;

  const resolveAffiliatePhone = (sources: {
    phoneNumber?: string | null;
    user?: any;
    a_phone_number?: string | null;
  }) => {
    const linkedUser = resolveAffiliateUser(sources.user);
    return (
      sources.phoneNumber?.trim() ||
      linkedUser?.u_phone_number?.trim() ||
      sources.a_phone_number?.trim() ||
      ""
    );
  };

  const loadAffiliateDetails = async (affiliate: any) => {
    if (!affiliate?.aId) return affiliate;

    const [detailResult, rowResult] = await Promise.allSettled([
      apolloClient.query({
        query: GET_AFFILIATE_BY_ID_DIRECT_INDIRECT,
        variables: { aId: affiliate.aId },
        fetchPolicy: "network-only",
      }),
      apolloClient.query({
        query: GET_AFFILIATE_ROW_BY_PK,
        variables: { aId: affiliate.aId },
        fetchPolicy: "network-only",
      }),
    ]);

    const detail =
      detailResult.status === "fulfilled"
        ? detailResult.value.data?.GetAffiliateById?.[0]
        : null;
    const row =
      rowResult.status === "fulfilled"
        ? rowResult.value.data?.affiliates_by_pk
        : null;

    const phoneNumber = resolveAffiliatePhone({
      phoneNumber: detail?.phoneNumber ?? affiliate.phoneNumber,
      user: row?.user,
      a_phone_number: row?.a_phone_number,
    });
    const linkedUser = resolveAffiliateUser(row?.user);
    const referralLink =
      detail?.referralUrl?.trim() ||
      row?.a_referral_url?.trim() ||
      linkedUser?.u_referral_link?.trim() ||
      affiliate.referralLink?.trim() ||
      affiliate.referralUrl?.trim() ||
      affiliate.referral_url?.trim() ||
      "";

    return {
      ...affiliate,
      emailId: detail?.emailId ?? affiliate.emailId,
      firstName: detail?.firstName ?? affiliate.firstName,
      lastName: detail?.lastName ?? affiliate.lastName,
      aType: detail?.aType ?? affiliate.aType,
      aStatus: detail?.aStatus ?? affiliate.aStatus,
      aOrganization:
        row?.a_organization ??
        detail?.aOrganization ??
        affiliate.aOrganization ??
        "",
      phoneNumber,
      referralLink,
      referralUrl: referralLink,
      isSupportVanderbiltNILFund:
        row?.a_vanderbilt_nil_fund ??
        detail?.isSupportVanderbiltNILFund ??
        affiliate.isSupportVanderbiltNILFund ??
        false,
    };
  };

  const syncAffiliateContactAfterCreate = async (
    email: string,
    firstName: string,
    lastName: string,
    organization: string,
    phoneNumber: string
  ) => {
    const org = organization?.trim();
    const phone = phoneNumber?.trim();
    if (!org && !phone) return;

    try {
      const { data } = await apolloClient.query({
        query: GET_AFFILIATES,
        variables: {
          limit: 5,
          offset: 0,
          searchText: email,
          aStatus: ["Active", "Deactive"],
          aType: ["Direct", "Indirect", "Both"],
          order_by: { aCreatedAt: "desc" },
        },
        fetchPolicy: "network-only",
      });
      const created = (data?.getAffiliates || []).find(
        (row: any) => (row.emailId || "").toLowerCase() === email.toLowerCase()
      );
      if (!created?.aId) return;

      await updateAffiliate({
        variables: {
          affiliateId: created.aId,
          firstName,
          lastName,
          emailId: email,
          organization: org || null,
          isActive: true,
          phoneNumber: phone || null,
        },
      });
    } catch (syncErr) {
      console.warn("Affiliate contact sync after create failed:", syncErr);
    }
  };

  const resolveLegacyAffiliateUser = (affiliate: any) => {
    const user = affiliate?.user;
    return Array.isArray(user) ? user[0] : user;
  };

  const resolveLegacyAffiliateUserId = (affiliate: any, linkedUser: any) => {
    const candidate =
      linkedUser?.uId ??
      linkedUser?.u_id ??
      linkedUser?.id ??
      affiliate?.uId ??
      affiliate?.u_id ??
      affiliate?.aId ??
      affiliate?.a_id ??
      null;
    return candidate ? String(candidate) : null;
  };

  const affiliateMetricsByKey = useMemo(() => {
    type AffiliateMetrics = {
      signupReferralEarnings?: number;
      totalAmount: number;
      directPayout: number;
      indirectPayout: number;
      sellTotalAmount: number;
      sellDirectPayout: number;
      sellIndirectPayout: number;
      buyTotalAmount: number;
      buyDirectPayout: number;
      buyIndirectPayout: number;
    };
    const map = new Map<string, AffiliateMetrics>(nodeAffiliateMetrics);
    const buyMetricsByUserId = new Map<
      string,
      Pick<AffiliateMetrics, "buyTotalAmount" | "buyDirectPayout" | "buyIndirectPayout">
    >();
    (buySubscriptionCommissionsData?.affiliate_buy_subscription_commissions || []).forEach((row: any) => {
      const userId = String(row?.absc_affiliate_user_id || "").trim();
      if (!userId) return;
      const amount = Number(row?.absc_commission_amount || 0) || 0;
      const level = String(row?.absc_commission_level || "").trim().toLowerCase();
      const current = buyMetricsByUserId.get(userId) || {
        buyTotalAmount: 0,
        buyDirectPayout: 0,
        buyIndirectPayout: 0,
      };

      current.buyTotalAmount += amount;
      if (level === "direct") {
        current.buyDirectPayout += amount;
      } else if (level === "indirect") {
        current.buyIndirectPayout += amount;
      }

      buyMetricsByUserId.set(userId, current);
    });
    const ingest = (rows: any[] | undefined) => {
      (rows || []).forEach((affiliate: any) => {
        // Preserve buy metrics already loaded from /api/affiliate/earnedMetrics.
        // Hasura getAffiliates is sell-only and must not wipe buy totals to 0.
        const existing =
          (affiliate?.aId ? map.get(`a:${affiliate.aId}`) : undefined) ||
          (affiliate?.uId ? map.get(`u:${affiliate.uId}`) : undefined);
        const graphQlBuyMetrics = affiliate?.uId
          ? buyMetricsByUserId.get(String(affiliate.uId).trim())
          : undefined;

        const hasExplicitSell =
          affiliate?.sellTotalAmount != null ||
          affiliate?.sellDirectPayout != null ||
          affiliate?.sellIndirectPayout != null;
        const sellTotalAmount = hasExplicitSell
          ? Number(affiliate?.sellTotalAmount ?? 0) || 0
          : Number(affiliate?.totalAmount ?? 0) || 0;
        const sellDirectPayout = hasExplicitSell
          ? Number(affiliate?.sellDirectPayout ?? 0) || 0
          : Number(affiliate?.directPayout ?? 0) || 0;
        const sellIndirectPayout = hasExplicitSell
          ? Number(affiliate?.sellIndirectPayout ?? 0) || 0
          : Number(affiliate?.indirectPayout ?? 0) || 0;

        const buyTotalAmount =
          Number(
            affiliate?.buyTotalAmount ??
              graphQlBuyMetrics?.buyTotalAmount ??
              existing?.buyTotalAmount ??
              0
          ) || 0;
        const buyDirectPayout =
          Number(
            affiliate?.buyDirectPayout ??
              graphQlBuyMetrics?.buyDirectPayout ??
              existing?.buyDirectPayout ??
              0
          ) || 0;
        const buyIndirectPayout =
          Number(
            affiliate?.buyIndirectPayout ??
              graphQlBuyMetrics?.buyIndirectPayout ??
              existing?.buyIndirectPayout ??
              0
          ) || 0;

        const metrics: AffiliateMetrics = {
          totalAmount: sellTotalAmount + buyTotalAmount,
          directPayout: sellDirectPayout + buyDirectPayout,
          indirectPayout: sellIndirectPayout + buyIndirectPayout,
          sellTotalAmount,
          sellDirectPayout,
          sellIndirectPayout,
          buyTotalAmount,
          buyDirectPayout,
          buyIndirectPayout,
        };
        if (affiliate?.aId) map.set(`a:${affiliate.aId}`, metrics);
        if (affiliate?.uId) map.set(`u:${affiliate.uId}`, metrics);
      });
    };
    // Always ingest summary + node metrics — even when the paginated query errors.
    ingest(summaryAffiliatesData?.getAffiliates);
    ingest(modernAffiliatesData?.getAffiliates);
    // The owner user's earnings are signup rewards, separate from getAffiliates commissions.
    (summaryLegacyAffiliatesData?.affiliates || []).forEach((affiliate: any) => {
      const owner = resolveLegacyAffiliateUser(affiliate);
      if (!owner) return;
      const aId = affiliate.aId;
      const uId = resolveLegacyAffiliateUserId(affiliate, owner);
      const current = map.get(`a:${aId}`) || map.get(`u:${uId}`) || emptyAffiliateMetrics();
      const metrics = includeSignupRewards(current, owner.totalEarnings ?? owner.u_total_earnings);
      if (aId) map.set(`a:${aId}`, metrics);
      if (uId) map.set(`u:${uId}`, metrics);
    });
    return map;
  }, [modernAffiliatesData, summaryAffiliatesData, summaryLegacyAffiliatesData, nodeAffiliateMetrics, buySubscriptionCommissionsData]);

  const lookupAffiliateMetrics = (aId: string | null | undefined, uId: string | null | undefined) => {
    if (aId && affiliateMetricsByKey.has(`a:${aId}`)) {
      return affiliateMetricsByKey.get(`a:${aId}`)!;
    }
    if (uId && affiliateMetricsByKey.has(`u:${uId}`)) {
      return affiliateMetricsByKey.get(`u:${uId}`)!;
    }
    return {
      totalAmount: 0,
      directPayout: 0,
      indirectPayout: 0,
      sellTotalAmount: 0,
      sellDirectPayout: 0,
      sellIndirectPayout: 0,
      buyTotalAmount: 0,
      buyDirectPayout: 0,
      buyIndirectPayout: 0,
    };
  };

  const enrichAffiliateRow = (affiliate: any) => {
    const metrics = lookupAffiliateMetrics(affiliate?.aId, affiliate?.uId);
    const rowTotal = Number(affiliate?.totalAmount) || 0;
    const rowDirect = Number(affiliate?.directPayout) || 0;
    const rowIndirect = Number(affiliate?.indirectPayout) || 0;
    const rowSellTotal = Number(affiliate?.sellTotalAmount) || rowTotal;
    const rowSellDirect = Number(affiliate?.sellDirectPayout) || rowDirect;
    const rowSellIndirect = Number(affiliate?.sellIndirectPayout) || rowIndirect;
    const rowBuyTotal = Number(affiliate?.buyTotalAmount) || 0;
    const rowBuyDirect = Number(affiliate?.buyDirectPayout) || 0;
    const rowBuyIndirect = Number(affiliate?.buyIndirectPayout) || 0;
    return {
      ...affiliate,
      totalAmount: metrics.totalAmount > 0 ? metrics.totalAmount : rowTotal,
      directPayout: metrics.directPayout > 0 ? metrics.directPayout : rowDirect,
      indirectPayout: metrics.indirectPayout > 0 ? metrics.indirectPayout : rowIndirect,
      sellTotalAmount: metrics.sellTotalAmount > 0 ? metrics.sellTotalAmount : rowSellTotal,
      sellDirectPayout: metrics.sellDirectPayout > 0 ? metrics.sellDirectPayout : rowSellDirect,
      sellIndirectPayout: metrics.sellIndirectPayout > 0 ? metrics.sellIndirectPayout : rowSellIndirect,
      buyTotalAmount: metrics.buyTotalAmount > 0 ? metrics.buyTotalAmount : rowBuyTotal,
      buyDirectPayout: metrics.buyDirectPayout > 0 ? metrics.buyDirectPayout : rowBuyDirect,
      buyIndirectPayout: metrics.buyIndirectPayout > 0 ? metrics.buyIndirectPayout : rowBuyIndirect,
    };
  };

  const normalizeLegacyAffiliateRows = (rows: any[] = []) =>
    rows.map((affiliate: any) => {
      const linkedUser = resolveLegacyAffiliateUser(affiliate);
      const isActive = linkedUser?.isActive ?? linkedUser?.u_is_active;
      const aId = affiliate?.aId || affiliate?.a_id || null;
      const uId = resolveLegacyAffiliateUserId(affiliate, linkedUser);
      const metrics = lookupAffiliateMetrics(aId, uId);
      return {
        aId,
        aType: affiliate?.aType || affiliate?.a_type || "Direct",
        totalAmount: metrics.totalAmount,
        directPayout: metrics.directPayout,
        emailId: affiliate?.emailId || "",
        firstName: affiliate?.firstName || "",
        indirectPayout: metrics.indirectPayout,
        lastName: affiliate?.lastName || "",
        noOfAffiliates: 0,
        noOfUsers: linkedUser?.noOfUsers?.aggregate?.count || 0,
        referralCode: linkedUser?.affiliateId || affiliate?.referralCode || affiliate?.referral_code || "",
        uId,
        userFullName: linkedUser?.userFullName || "",
        aStatus: isActive === false ? "Deactive" : "Active",
        aCreatedAt: null,
        aUpdatedAt: null,
        aOrganization: affiliate?.aOrganization || affiliate?.a_organization || "",
      };
    });

  const rawAffiliates = useMemo(
    () => {
      const rows = shouldUseLegacyAffiliateData
        ? normalizeLegacyAffiliateRows(legacyAffiliatesData?.affiliates || [])
        : (modernAffiliatesData?.getAffiliates || []);
      return rows.map(enrichAffiliateRow);
    },
    [
      shouldUseLegacyAffiliateData,
      legacyAffiliatesData,
      modernAffiliatesData,
      affiliateMetricsByKey,
    ]
  );
  const rawSummaryAffiliates = useMemo(
    () => {
      const rows = shouldUseLegacyAffiliateData
        ? normalizeLegacyAffiliateRows(summaryLegacyAffiliatesData?.affiliates || [])
        : (summaryAffiliatesData?.getAffiliates || []);
      return rows.map(enrichAffiliateRow);
    },
    [
      shouldUseLegacyAffiliateData,
      summaryLegacyAffiliatesData,
      summaryAffiliatesData,
      affiliateMetricsByKey,
    ]
  );

  const salesRepsList = salesRepsData?.salesReps || [];
  const scopedSalesRepId = useMemo(() => {
    if (!isMarketingUser || !loggedInEmail) return null;
    const rep = salesRepsList.find(
      (salesRep: any) => (salesRep?.sr_email || "").toLowerCase().trim() === loggedInEmail
    );
    return rep?.sr_id || null;
  }, [isMarketingUser, loggedInEmail, salesRepsList]);

  const scopedAffiliateIds = useMemo(() => {
    if (!isMarketingUser || !scopedSalesRepId) return new Set<string>();
    const ids = (allUsersData?.user || [])
      .filter((user: any) => user?.u_sales_reps_id === scopedSalesRepId && !!user?.u_affiliate_id)
      .map((user: any) => user?.u_affiliate_id)
      .filter(Boolean);
    return new Set<string>(ids);
  }, [isMarketingUser, scopedSalesRepId, allUsersData]);

  useEffect(() => {
    if (!isMarketingUser || scopedAffiliateIds.size === 0) return;
    const idsToResolve = Array.from(scopedAffiliateIds).filter((id) => !affiliateMetaById[id]);
    if (!idsToResolve.length) return;
    let cancelled = false;

    (async () => {
      const results = await Promise.all(
        idsToResolve.map(async (aId) => {
          try {
            const { data } = await apolloClient.query({
              query: GET_AFFILIATE_BY_ID_DIRECT_INDIRECT,
              variables: { aId },
              fetchPolicy: "network-only",
            });
            const item = data?.GetAffiliateById?.[0];
            if (!item) return null;
            return [aId, item] as const;
          } catch (_err) {
            return null;
          }
        })
      );
      if (cancelled) return;
      const resolved = results.filter((entry): entry is readonly [string, any] => !!entry);
      if (!resolved.length) return;
      setAffiliateMetaById((prev) => {
        const next = { ...prev };
        resolved.forEach(([aId, item]) => {
          next[aId] = item;
        });
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [isMarketingUser, scopedAffiliateIds, affiliateMetaById, apolloClient]);

  const marketingAffiliatesFromUsers = useMemo(() => {
    if (!isMarketingUser || !scopedSalesRepId) return [];
    const users = (allUsersData?.user || []).filter(
      (user: any) =>
        user?.u_sales_reps_id === scopedSalesRepId &&
        !!user?.u_affiliate_id &&
        user?.u_delete_requested !== true
    );
    const byAffiliate = new Map<string, any>();
    users.forEach((user: any) => {
      const affiliateId = user?.u_affiliate_id;
      const existing = byAffiliate.get(affiliateId) || {
        aId: affiliateId,
        firstName: user?.affiliate?.a_first_name || affiliateMetaById?.[affiliateId]?.firstName || "",
        lastName: user?.affiliate?.a_last_name || affiliateMetaById?.[affiliateId]?.lastName || "",
        emailId: user?.affiliate?.a_email_id || affiliateMetaById?.[affiliateId]?.emailId || "",
        referralCode: "",
        aStatus: affiliateMetaById?.[affiliateId]?.aStatus || "Active",
        aType: affiliateMetaById?.[affiliateId]?.aType || "Direct",
        noOfUsers: 0,
        totalAmount: 0,
        directPayout: 0,
        aOrganization: affiliateMetaById?.[affiliateId]?.aOrganization || "",
      };
      existing.noOfUsers += 1;
      byAffiliate.set(affiliateId, existing);
    });
    return Array.from(byAffiliate.values());
  }, [isMarketingUser, scopedSalesRepId, allUsersData, affiliateMetaById]);

  // ========== CLIENT-SIDE FILTERING FOR LEGACY MODE ==========
  const applyClientFilters = (list: any[]) => {
    if (!shouldUseLegacyAffiliateData) return list;
    return list.filter((affiliate) => {
      // Status filter
      if (statusFilter !== "All" && affiliate.aStatus !== statusFilter) return false;
      // Type filter
      if (typeFilter !== "All" && affiliate.aType !== typeFilter) return false;
      return true;
    });
  };

  const affiliates = useMemo(() => {
    let baseList: any[] = [];
    if (!isMarketingUser) {
      baseList = rawAffiliates;
    } else if (scopedAffiliateIds.size) {
      baseList = rawAffiliates.filter((affiliate: any) => scopedAffiliateIds.has(affiliate?.aId));
      if (baseList.length === 0) baseList = marketingAffiliatesFromUsers;
    } else {
      baseList = marketingAffiliatesFromUsers;
    }
    // Apply client-side filtering if using legacy data
    return applyClientFilters(baseList);
  }, [isMarketingUser, rawAffiliates, scopedAffiliateIds, marketingAffiliatesFromUsers, statusFilter, typeFilter, shouldUseLegacyAffiliateData]);

  const summaryAffiliates = useMemo(() => {
    if (!isMarketingUser) return rawSummaryAffiliates;
    if (!scopedAffiliateIds.size) return marketingAffiliatesFromUsers;
    const filtered = rawSummaryAffiliates.filter((affiliate: any) => scopedAffiliateIds.has(affiliate?.aId));
    const finalList = filtered.length > 0 ? filtered : marketingAffiliatesFromUsers;
    // We do NOT apply client filtering to summaryAffiliates because stats should show total numbers
    return finalList;
  }, [isMarketingUser, rawSummaryAffiliates, scopedAffiliateIds, marketingAffiliatesFromUsers]);

  const referredAffiliates = referredAffiliatesData?.affiliates || [];

  const filteredReferredAffiliates = useMemo(() => {
    const availableAffiliates = referredAffiliates.filter((affiliate: any) => affiliate.a_id !== selected?.aId);
    const query = referredAffiliateSearch.trim().toLowerCase();
    if (!query) return availableAffiliates;

    return availableAffiliates.filter((affiliate: any) => {
      const firstName = String(affiliate?.user?.u_first_name || affiliate?.firstName || "").toLowerCase();
      const lastName = String(affiliate?.user?.u_last_name || affiliate?.lastName || "").toLowerCase();
      const email = String(affiliate?.user?.u_email_id || affiliate?.emailId || "").toLowerCase();
      const org = String(affiliate?.aOrganization || affiliate?.organization || "").toLowerCase();
      const code = String(affiliate?.referralCode || affiliate?.a_referral_code || "").toLowerCase();
      return (
        `${firstName} ${lastName}`.includes(query) ||
        email.includes(query) ||
        org.includes(query) ||
        code.includes(query)
      );
    });
  }, [referredAffiliates, referredAffiliateSearch, selected?.aId]);

  const visibleAffiliateCount = affiliates.length;
  const isDefaultAffiliateFilter =
    statusFilter === "All" &&
    typeFilter === "All" &&
    searchText === "%";

  const effectiveAffiliateTotalCount = isDefaultAffiliateFilter
    ? (isMarketingUser
        ? summaryAffiliates.length
        : (
          shouldUseLegacyAffiliateData
            ? (summaryLegacyAffiliatesData?.totalAffiliates?.aggregate?.count || summaryAffiliates.length || affiliateTotalCount)
            : (summaryCountData?.GetAffiliatesCount?.totalCount || summaryAffiliates.length || affiliateTotalCount)
          ))
    : (isMarketingUser ? affiliates.length : affiliateTotalCount);

  const getAffiliateDisplayLabel = (affiliate: any) => {
    const fullName = `${affiliate?.firstName || ""} ${affiliate?.lastName || ""}`.trim();
    if (fullName) return fullName;
    if (affiliate?.emailId) return affiliate.emailId;
    if (affiliate?.aOrganization) return affiliate.aOrganization;
    if (affiliate?.referralCode) return affiliate.referralCode;
    if (affiliate?.aId) return `Affiliate ${String(affiliate.aId).slice(0, 8)}`;
    return "Affiliate";
  };
  
  let affiliateUsers: any[] = [];
  const rawUsers = usersData?.getAffiliateUsers;
  if (Array.isArray(rawUsers?.users)) {
    affiliateUsers = rawUsers.users;
  } else if (Array.isArray(rawUsers?.data)) {
    affiliateUsers = rawUsers.data;
  } else if (Array.isArray(rawUsers)) {
    affiliateUsers = rawUsers;
  }
  
  const rawPayouts = payoutsData?.getAffiliatePayouts;
  const affiliatePayouts =
    (Array.isArray(rawPayouts?.payouts) && rawPayouts.payouts) ||
    (Array.isArray(rawPayouts?.data) && rawPayouts.data) ||
    (Array.isArray(rawPayouts) && rawPayouts) ||
    [];

  const computedSelectedMetrics = useMemo(() => {
    if (!selected) {
      return {
        noOfUsers: 0,
        totalAmount: 0,
        directPayout: 0,
        indirectPayout: 0,
        sellTotalAmount: 0,
        sellDirectPayout: 0,
        sellIndirectPayout: 0,
        buyTotalAmount: 0,
        buyDirectPayout: 0,
        buyIndirectPayout: 0,
      };
    }

    const usersFromAffiliateQuery = Array.isArray(affiliateUsers) ? affiliateUsers : [];
    const assigned = Array.isArray(assignedUsers) ? assignedUsers : [];

    const uniqueUserIds = new Set<string>();
    usersFromAffiliateQuery.forEach((u: any) => {
      const id = u?.u_id || u?.uId;
      if (id) uniqueUserIds.add(String(id));
    });
    assigned.forEach((u: any) => {
      if (u?.u_id) uniqueUserIds.add(String(u.u_id));
    });

    const noOfUsers = uniqueUserIds.size;

    const fallbackNoOfUsers = Number(selected?.noOfUsers ?? selected?.no_of_users ?? 0) || 0;
    const reportedUserCount = usersData?.getAffiliateUsers?.aggregate?.count;
    const hasReportedUserCount = reportedUserCount !== null && reportedUserCount !== undefined;
    const selectedMetrics = lookupAffiliateMetrics(selected?.aId, selected?.uId);
    const totalAmount =
      selectedMetrics.totalAmount ||
      Number(selected?.totalAmount ?? selected?.total_amount ?? 0) ||
      0;
    const directPayout =
      selectedMetrics.directPayout ||
      Number(
        selected?.directPayout ?? selected?.direct_payout ?? selected?.directPayouts ?? selected?.directpayout ?? 0
      ) ||
      0;
    const indirectPayout =
      selectedMetrics.indirectPayout ||
      Number(selected?.indirectPayout ?? selected?.indirect_payout ?? 0) ||
      0;

    return {
      noOfUsers: hasReportedUserCount
        ? Math.max(noOfUsers, Number(reportedUserCount) || 0)
        : noOfUsers || fallbackNoOfUsers,
      totalAmount,
      directPayout,
      indirectPayout,
      sellTotalAmount:
        selectedMetrics.sellTotalAmount || Number(selected?.sellTotalAmount ?? selected?.totalAmount ?? 0) || 0,
      sellDirectPayout:
        selectedMetrics.sellDirectPayout || Number(selected?.sellDirectPayout ?? selected?.directPayout ?? 0) || 0,
      sellIndirectPayout:
        selectedMetrics.sellIndirectPayout ||
        Number(selected?.sellIndirectPayout ?? selected?.indirectPayout ?? 0) ||
        0,
      buyTotalAmount: selectedMetrics.buyTotalAmount || Number(selected?.buyTotalAmount ?? 0) || 0,
      buyDirectPayout: selectedMetrics.buyDirectPayout || Number(selected?.buyDirectPayout ?? 0) || 0,
      buyIndirectPayout: selectedMetrics.buyIndirectPayout || Number(selected?.buyIndirectPayout ?? 0) || 0,
    };
  }, [selected, affiliateUsers, assignedUsers, affiliateMetricsByKey, usersData]);

  const mergedAffiliateUsers = useMemo(() => {
    const map = new Map<string, any>();
    const pushUser = (u: any) => {
      const id = u?.u_id || u?.uId || u?.u_id || u?.id || u?.uId || u?.u_id;
      if (!id) return;
      if (!map.has(String(id))) {
        map.set(String(id), {
          u_id: id,
          u_full_name: u?.u_full_name || `${u?.u_first_name || u?.firstName || ''} ${u?.u_last_name || u?.lastName || ''}`.trim() || '-',
          u_email_id: u?.u_email_id || u?.emailId || '-',
          u_phone_number: u?.u_phone_number || u?.phoneNumber || '-',
          u_created_at: u?.u_created_at || u?.createdAt || null,
          u_is_active: (u?.u_is_active ?? u?.isActive) ?? true,
        });
      }
    };

    (affiliateUsers || []).forEach(pushUser);
    (assignedUsers || []).forEach(pushUser);

    return Array.from(map.values());
  }, [affiliateUsers, assignedUsers]);

  useEffect(() => {
    setAffiliatePageOffset(0);
  }, [statusFilter, typeFilter, searchText]);

  useEffect(() => {
    setAffiliateTotalCount(affiliateCountData?.GetAffiliatesCount?.totalCount || 0);
  }, [affiliateCountData]);
  useEffect(() => {
    if (!shouldUseLegacyAffiliateData) return;
    setAffiliateTotalCount(legacyAffiliatesData?.totalAffiliates?.aggregate?.count || 0);
  }, [shouldUseLegacyAffiliateData, legacyAffiliatesData]);

  useEffect(() => {
    if (activeTab === "users" && selected?.aId) {
      setUserPageOffset(0);
      refetchUsers({ affiliateId: selected.aId, limit: pageSize, offset: 0 });
      refetchAssignedUsers({
        affiliateIds: [selected.aId, selected.uId, selected?.user?.uId, selected?.user?.id].filter(Boolean),
        affiliateTextIds: [selected.aId, selected.uId, selected?.user?.uId, selected?.user?.id, selected?.referralCode, selected?.referral_code].filter(Boolean).map(String),
      });
    }
  }, [activeTab, selected?.aId]);

  useEffect(() => {
    if (activeTab === "payouts" && selected?.aId) {
      setPayoutPageOffset(0);
      refetchPayouts({ affiliateId: selected.aId, limit: pageSize, offset: 0 });
    }
  }, [activeTab, selected?.aId]);

  useEffect(() => {
    if (!selected || isEditing) return;
    const listRow = affiliates.find((a: any) => a.aId === selected.aId);
    if (!listRow) {
      setSelected(null);
      return;
    }
    setSelected((prev: typeof selected) => {
      if (!prev || prev.aId !== listRow.aId) return listRow;
      return {
        ...listRow,
        // Detail panel loads type/status via GetAffiliateById — don't clobber with stale list data.
        aType: prev.aType ?? listRow.aType,
        aStatus: prev.aStatus ?? listRow.aStatus,
        aOrganization: listRow.aOrganization?.trim() || prev.aOrganization || "",
        phoneNumber: listRow.phoneNumber?.trim() || prev.phoneNumber || "",
        referralLink: prev.referralLink || prev.referralUrl || listRow.referralLink || listRow.referralUrl || "",
        referralUrl: prev.referralUrl || prev.referralLink || listRow.referralUrl || listRow.referralLink || "",
        isSupportVanderbiltNILFund:
          listRow.isSupportVanderbiltNILFund ?? prev.isSupportVanderbiltNILFund,
      };
    });
  }, [affiliates, selected?.aId, isEditing]);

  useEffect(() => {
    loadAssignedUsersCount();
    loadAffiliateEarnedMetrics();
  }, []);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@([^\s@]+\.)+[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumber = (phone: string): boolean => {
    if (!phone || phone.trim() === "") return false;
    const cleanPhone = phone.replace(/\D/g, '');
    return cleanPhone.length >= 10 && cleanPhone.length <= 15;
  };

  const formatPhoneNumber = (value: string): string => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const validateUrlSuffix = (suffix: string): boolean => {
    if (!suffix) return true;
    const urlRegex = /^[a-zA-Z0-9-]+$/;
    return urlRegex.test(suffix);
  };

  const validateAddAffiliateForm = (): boolean => {
    const errors: any = {};
    
    if (!newAffiliateData.firstName.trim()) {
      errors.firstName = "First name is required";
    } else if (newAffiliateData.firstName.length < 2) {
      errors.firstName = "First name must be at least 2 characters";
    }
    
    if (!newAffiliateData.lastName.trim()) {
      errors.lastName = "Last name is required";
    } else if (newAffiliateData.lastName.length < 2) {
      errors.lastName = "Last name must be at least 2 characters";
    }
    
    if (!newAffiliateData.emailId.trim()) {
      errors.emailId = "Email is required";
    } else if (!validateEmail(newAffiliateData.emailId)) {
      errors.emailId = "Please enter a valid email address";
    }
    
    if (!newAffiliateData.phoneNumber.trim()) {
      errors.phoneNumber = "Phone number is required";
    } else if (!validatePhoneNumber(newAffiliateData.phoneNumber)) {
      errors.phoneNumber = "Please enter a valid phone number (minimum 10 digits)";
    }
    
    if (newAffiliateData.urlSuffix && !validateUrlSuffix(newAffiliateData.urlSuffix)) {
      errors.urlSuffix = "URL suffix can only contain letters, numbers, and hyphens";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditClick = () => {
    if (!canManageAffiliates) {
      toast.error("Only admin can edit affiliates");
      return;
    }
    if (selected) {
      setEditData({
        ...selected,
        isNonProfitOrganization: Boolean(selected.isSupportVanderbiltNILFund),
      });
      setIsEditing(true);
    }
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setEditData({});
  };

  const refreshAffiliateData = async () => {
    const aStatus = statusFilter === "All" ? ["Active", "Deactive"] : [statusFilter];
    const aType = typeFilter === "All" ? ["Direct", "Indirect", "Both"] : [typeFilter];

    return Promise.allSettled([
      refetch({
        limit,
        offset: affiliatePageOffset,
        searchText,
        aStatus,
        aType,
        order_by: { aCreatedAt: "desc" },
      }),
      refetchAffiliateCount({
        searchText,
        aStatus: statusFilter === "All" ? null : statusFilter,
        aType: typeFilter === "All" ? null : typeFilter,
        referrerId: null,
      }),
      refetchSummaryAffiliates(),
      refetchSummaryCount(),
      loadAssignedUsersCount(),
      loadAffiliateEarnedMetrics(),
    ]);
  };

  const handleRefresh = async () => {
    await refreshAffiliateData();
  };

const handleSaveClick = async () => {
  if (!canManageAffiliates) {
    toast.error("Only admin can edit affiliates");
    return;
  }
  if (!selected || !editData) return;
  
  const emailToValidate = editData.emailId ?? selected.emailId;
  if (!validateEditEmail(emailToValidate)) {
    toast.error("Please enter a valid email address");
    return;
  }
  
  const phoneToValidate = editData.phoneNumber ?? selected.phoneNumber;
  if (!validateEditPhoneNumber(phoneToValidate)) {
    toast.error("Please enter a valid phone number (minimum 10 digits)");
    return;
  }
  
  setIsUpdatingAffiliate(true);
  const loadingToast = toast.loading("Updating affiliate...");

  try {
    const organization = (editData.aOrganization ?? selected.aOrganization ?? "").trim();
    const isNonProfit = editData.isNonProfitOrganization ?? selected.isSupportVanderbiltNILFund ?? false;
    const savedPhone = (editData.phoneNumber ?? selected.phoneNumber)?.trim() || null;

    const [updateResponse, editNodeResult] = await Promise.all([
      updateAffiliate({
        variables: {
          affiliateId: selected.aId,
          firstName: editData.firstName ?? selected.firstName,
          lastName: editData.lastName ?? selected.lastName,
          emailId: editData.emailId ?? selected.emailId,
          organization: organization || null,
          isActive: (editData.aStatus ?? selected.aStatus) === "Active",
          phoneNumber: savedPhone,
        },
      }),
      editAffiliateViaNodeApi({
        affiliateId: selected.aId,
        firstName: editData.firstName ?? selected.firstName,
        lastName: editData.lastName ?? selected.lastName,
        aType: editData.aType ?? selected.aType,
        isActive: (editData.aStatus ?? selected.aStatus) === "Active",
        organization: organization || null,
        phoneNumber: savedPhone,
        vanderbiltNILFund: resolveNonProfitFundFlag(isNonProfit, organization),
      }),
    ]);

    const updateError = updateResponse.errors?.[0]?.message;
    const updateOk = Boolean(updateResponse.data?.affiliate);
    const editOk = Boolean(editNodeResult?.message);

    // Check for duplicate key violations
    if (updateError) {
      const errorLower = updateError.toLowerCase();
      if (errorLower.includes("duplicate key") || errorLower.includes("unique constraint")) {
        if (errorLower.includes("email")) {
          toast.update(loadingToast, {
            render: "This email is already registered to another affiliate. Please use a different email address.",
            type: "error",
            isLoading: false,
            autoClose: 5000
          });
        } else if (errorLower.includes("phone")) {
          toast.update(loadingToast, {
            render: "This phone number is already registered to another affiliate. Please use a different phone number.",
            type: "error",
            isLoading: false,
            autoClose: 5000
          });
        } else {
          toast.update(loadingToast, {
            render: "Duplicate entry detected. Please check if the email or phone number is already in use.",
            type: "error",
            isLoading: false,
            autoClose: 5000
          });
        }
        setIsUpdatingAffiliate(false);
        return;
      }
    }

    if (!updateOk && !editOk) {
      throw new Error(updateError || "Failed to update affiliate");
    }

    toast.update(loadingToast, {
      render: "Affiliate updated successfully!",
      type: "success",
      isLoading: false,
      autoClose: 3000
    });

    const savedOrg = organization;
    const savedNonProfit = resolveNonProfitFundFlag(isNonProfit, organization);
    const refreshed = await loadAffiliateDetails({
      ...selected,
      aOrganization: savedOrg,
      phoneNumber: savedPhone || "",
      isSupportVanderbiltNILFund: savedNonProfit,
    });
    setSelected({
      ...refreshed,
      aType: editData.aType ?? refreshed.aType ?? selected.aType,
      aStatus: editData.aStatus ?? refreshed.aStatus ?? selected.aStatus,
      aOrganization: savedOrg || refreshed.aOrganization,
      phoneNumber: savedPhone || refreshed.phoneNumber || "",
      isSupportVanderbiltNILFund: savedNonProfit,
    });

    void Promise.all([
      refetch(),
      refetchAffiliateSummaryData(),
      loadAssignedUsersCount(),
    ]);
    setIsEditing(false);
    setEditData({});

  } catch (err: any) {
    console.error("Update error:", err);
    
    // Check for duplicate key violations in the error message
    const errorMessage = err.message || String(err);
    const errorLower = errorMessage.toLowerCase();
    
    if (errorLower.includes("duplicate key") || errorLower.includes("unique constraint")) {
      let userFriendlyMessage = "Duplicate entry detected. Please check if the email or phone number is already in use.";
      
      if (errorLower.includes("email")) {
        userFriendlyMessage = "This email is already registered to another affiliate. Please use a different email address.";
      } else if (errorLower.includes("phone")) {
        userFriendlyMessage = "This phone number is already registered to another affiliate. Please use a different phone number.";
      }
      
      toast.update(loadingToast, {
        render: userFriendlyMessage,
        type: "error",
        isLoading: false,
        autoClose: 5000
      });
    } else {
      toast.update(loadingToast, {
        render: err.message || "Failed to update affiliate. Please try again.",
        type: "error",
        isLoading: false,
        autoClose: 5000
      });
    }
  } finally {
    setIsUpdatingAffiliate(false);
  }
};
 const handleAddAffiliate = async (e: React.FormEvent) => {
  e.preventDefault();
  if (!canManageAffiliates) {
    toast.error("Only admin can add affiliates");
    return;
  }
  
  if (!validateAddAffiliateForm()) {
    toast.error("Please fix the validation errors before submitting");
    return;
  }

  setIsAddingAffiliate(true);
  const loadingToast = toast.loading("Creating affiliate...");

  try {
    const generatedReferralCode = `${newAffiliateData.firstName.substring(0, 3)}${newAffiliateData.lastName.substring(0, 3)}${Math.random().toString(36).substring(2, 8)}`.toLowerCase();
    const urlSuffix = (newAffiliateData.urlSuffix?.trim() || generatedReferralCode).toLowerCase();
    let referralLink = newAffiliateData.referralLink?.trim() || "";
    if (!referralLink) {
      referralLink = (await generateDynamicLink(urlSuffix)) || "";
    }
    if (!referralLink) {
      throw new Error("Failed to generate referral link. Please try again.");
    }

    let referredByValue = null;
    if (newAffiliateData.referredBy && newAffiliateData.referredBy !== "") {
      const foundAffiliate = referredAffiliates.find((affiliate: any) => 
        String(affiliate.a_id) === String(newAffiliateData.referredBy)
      );
      if (foundAffiliate && foundAffiliate.user && foundAffiliate.user.u_id) {
        referredByValue = foundAffiliate.user.u_id;
      }
    }

    const response = await createAffiliateViaNodeApi({
      firstname: newAffiliateData.firstName.trim(),
      lastname: newAffiliateData.lastName.trim(),
      emailid: newAffiliateData.emailId.trim(),
      phoneNumber: newAffiliateData.phoneNumber?.trim() || undefined,
      referralcode: urlSuffix,
      referrallink: referralLink,
      aType: newAffiliateData.aType,
      isVanderbiltNILFund: resolveNonProfitFundFlag(
        newAffiliateData.isNonProfitOrganization,
        newAffiliateData.organization
      ),
      referredBy: referredByValue,
      organization: newAffiliateData.organization?.trim() || "",
    });

    if (response?.message) {
      await syncAffiliateContactAfterCreate(
        newAffiliateData.emailId.trim(),
        newAffiliateData.firstName.trim(),
        newAffiliateData.lastName.trim(),
        newAffiliateData.organization?.trim() || "",
        newAffiliateData.phoneNumber?.trim() || ""
      );

      toast.update(loadingToast, {
        render: "Affiliate added successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000
      });
      
      setNewAffiliateData({
        firstName: "",
        lastName: "",
        emailId: "",
        phoneNumber: "",
        organization: "",
        aType: "Direct",
        referralCode: "",
        referralLink: "",
        isNonProfitOrganization: false,
        referredBy: null,
        referredByItem: null,
        urlSuffix: ""
      });
      setValidationErrors({});
      setShowAddAffiliate(false);

      void Promise.all([
        refetch(),
        refetchAffiliateSummaryData(),
        loadAssignedUsersCount(),
      ]);
    } else {
      toast.update(loadingToast, {
        render: "Failed to add affiliate. Please try again.",
        type: "error",
        isLoading: false,
        autoClose: 5000
      });
    }
  } catch (err: any) {
    console.error("Add affiliate error:", err);
    
    const errorMessage = err.message || String(err);
    const errorLower = errorMessage.toLowerCase();

    const isSesEmailError =
      errorMessage.includes("Email address is not verified") ||
      errorMessage.includes("identities failed the check");

    if (isSesEmailError) {
      toast.update(loadingToast, {
        render: "Affiliate added successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000
      });

      setNewAffiliateData({
        firstName: "",
        lastName: "",
        emailId: "",
        phoneNumber: "",
        organization: "",
        aType: "Direct",
        referralCode: "",
        referralLink: "",
        isNonProfitOrganization: false,
        referredBy: null,
        referredByItem: null,
        urlSuffix: ""
      });
      setValidationErrors({});
      setShowAddAffiliate(false);

      await Promise.all([
        refetch(),
        refetchAffiliateSummaryData(),
        loadAssignedUsersCount(),
      ]);
    } else if (errorLower.includes("duplicate key") || errorLower.includes("unique constraint")) {
      let userFriendlyMessage = "Unable to create affiliate. The email or phone number may already be in use.";
      
      if (errorLower.includes("email")) {
        userFriendlyMessage = "This email is already registered to another affiliate. Please use a different email address.";
      } else if (errorLower.includes("phone")) {
        userFriendlyMessage = "This phone number is already registered to another affiliate. Please use a different phone number.";
      } else if (errorLower.includes("affiliate link/code already exists")) {
        userFriendlyMessage = "An affiliate with this referral link already exists. Please try again.";
      }
      
      toast.update(loadingToast, {
        render: userFriendlyMessage,
        type: "error",
        isLoading: false,
        autoClose: 5000
      });
    } else if (errorLower.includes("network")) {
      toast.update(loadingToast, {
        render: "Network error. Please check your connection.",
        type: "error",
        isLoading: false,
        autoClose: 5000
      });
    } else {
      toast.update(loadingToast, {
        render: errorMessage || "Failed to add affiliate. Please try again.",
        type: "error",
        isLoading: false,
        autoClose: 5000
      });
    }
  } finally {
    setIsAddingAffiliate(false);
  }
};

  // Stats (unfiltered totals)
  const totalAffiliates = summaryCountData?.GetAffiliatesCount?.totalCount || summaryAffiliates.length;
  const activeAffiliates = summaryAffiliates.filter((a: any) => a?.aStatus === "Active").length;
  const inactiveAffiliates = summaryAffiliates.filter((a: any) => a?.aStatus === "Deactive").length;
  const totalUsersFromAffiliateSummary = summaryAffiliates.reduce(
    (total: number, affiliate: any) => total + (affiliate?.noOfUsers || 0),
    0
  );
  const totalUsers = Math.max(totalAssignedUsersCount, totalUsersFromAffiliateSummary);
  const totalEarned = summaryAffiliates.reduce((t: any, a: any) => t + (a?.totalAmount || 0), 0);
  const totalPayouts = summaryAffiliates.reduce((t: any, a: any) => t + (a?.directPayout || 0), 0);

  const userTotalPages = Math.ceil(userTotalCount / pageSize);
  const payoutTotalPages = Math.ceil(payoutTotalCount / pageSize);
  const affiliateTotalPages = Math.ceil(effectiveAffiliateTotalCount / limit);
  const affiliateCurrentPage = Math.floor(affiliatePageOffset / limit) + 1;
  const userCurrentPage = Math.floor(userPageOffset / pageSize) + 1;
  const payoutCurrentPage = Math.floor(payoutPageOffset / pageSize) + 1;

  const getReferralLink = () => {
    return shortReferralLink;
  };

  const copyTextToClipboard = async (text: string): Promise<boolean> => {
    if (!text) return false;
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (error) {}
    }
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.setAttribute("readonly", "");
    textArea.style.position = "fixed";
    textArea.style.top = "-9999px";
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    let copiedSuccessfully = false;
    try {
      copiedSuccessfully = document.execCommand("copy");
    } catch (error) {}
    document.body.removeChild(textArea);
    return copiedSuccessfully;
  };

  const generateReferralLink = async () => {
    setIsGeneratingLink(true);
    setGeneratedLink("");
    try {
      const suffix = (newAffiliateData.urlSuffix?.trim() || `${newAffiliateData.firstName.substring(0, 3)}${newAffiliateData.lastName.substring(0, 3)}${Math.random().toString(36).substring(2, 8)}`).toLowerCase();
      const referralLink = await generateDynamicLink(suffix);
      if (!referralLink) {
        throw new Error("Failed to generate referral link. Try again.");
      }
      setGeneratedLink(referralLink);
      setNewAffiliateData({ ...newAffiliateData, urlSuffix: suffix, referralLink });
      toast.info("Link generated successfully!");
    } catch (err: any) {
      toast.error(err?.message || "Failed to generate referral link. Try again.");
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const buildReferralSuffix = (affiliate: any, preferExistingCode = true) => {
    if (preferExistingCode && affiliate?.referralCode?.trim()) {
      return affiliate.referralCode.trim().toLowerCase();
    }
    return `${(affiliate?.firstName || "").slice(0, 3)}${(affiliate?.lastName || "").slice(0, 3)}${Math.random().toString(36).slice(2, 8)}`.toLowerCase();
  };

  const createBranchReferralLink = async (initialSuffix: string) => {
    try {
      const referralLink = await generateDynamicLink(initialSuffix);
      if (!referralLink) {
        throw new Error("Failed to generate referral link. Try again.");
      }
      return { referralLink, suffix: initialSuffix };
    } catch (err: any) {
      const message = err?.message || "";
      if (!message.includes("URL Suffix already exists")) {
        throw err;
      }
      const fallbackSuffix = `${initialSuffix.slice(0, 6)}${Math.random().toString(36).slice(2, 8)}`.toLowerCase();
      const referralLink = await generateDynamicLink(fallbackSuffix);
      if (!referralLink) {
        throw new Error("Failed to generate referral link. Try again.");
      }
      return { referralLink, suffix: fallbackSuffix };
    }
  };

  const handleGenerateLinkForSelected = async () => {
    const existing = originalReferralLink;
    if (existing) {
      setShowReferralPopup(true);
      return;
    }
    if (!selected?.aId) return;
    if (selected.aType === "Indirect") {
      toast.error("Indirect affiliates do not use referral links.");
      return;
    }

    setIsGeneratingLink(true);
    try {
      const { referralLink, suffix } = await createBranchReferralLink(
        buildReferralSuffix(selected, true)
      );

      await editAffiliateViaNodeApi({
        affiliateId: selected.aId,
        firstName: selected.firstName,
        lastName: selected.lastName,
        aType: selected.aType,
        isActive: selected.aStatus === "Active",
        organization: selected.aOrganization || null,
        phoneNumber: selected.phoneNumber || null,
        vanderbiltNILFund: resolveNonProfitFundFlag(
          Boolean(selected.isSupportVanderbiltNILFund),
          selected.aOrganization
        ),
        referralLink,
        referralCode: suffix,
      });

      setSelected((prev: typeof selected) =>
        prev
          ? {
              ...prev,
              referralLink,
              referralUrl: referralLink,
              referralCode: suffix,
            }
          : prev
      );
      setShowReferralPopup(true);
      toast.success("Link generated successfully!");
    } catch (err: any) {
      toast.error(err?.message || "Failed to generate referral link. Try again.");
    } finally {
      setIsGeneratingLink(false);
    }
  };

  return (
    <div className={`${styles.page} ${isDarkMode ? styles.dark : ""}`}>
      <div className={styles.main}>
        {/* HEADER */}
        <div className={styles.header}>
          <div>
            <p className={styles.subtitle}>Affiliate Management</p>
            <h1 className={styles.title}>Affiliates</h1>
          </div>
          <div className={styles.searchSection}>
            <button className={styles.iconBtn} onClick={handleRefresh}>
              <RefreshRoundedIcon fontSize="small" />
            </button>
            <button className={styles.iconBtn} onClick={() => setIsDarkMode(!isDarkMode)}>
              {isDarkMode ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
            </button>
            {canManageAffiliates && (
              <button
                className={styles.primaryBtn}
                style={{ marginLeft: 10, background: '#2563eb', color: '#fff', fontWeight: 500, fontSize: 14, padding: '10px 18px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8 }}
                onClick={() => setShowAddAffiliate(true)}
              >
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Add Affiliate
              </button>
            )}
          </div>
        </div>

        {/* Warning banner when filters are applied client-side */}
        {shouldUseLegacyAffiliateData && (statusFilter !== "All" || typeFilter !== "All") && (
          <div className={styles.warningBanner} style={{ backgroundColor: isDarkMode ? '#3b2e1e' : '#fef9c3', color: isDarkMode ? '#fde047' : '#854d0e', padding: '8px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>⚠️</span> Filters are applied client-side because the backend query is unavailable. Performance may be affected with many affiliates.
          </div>
        )}

        {/* STATS CARDS */}
        <div className={styles.statsContainer}>
          <div className={styles.statCard}>
            <div className={styles.statNumber}>{totalAffiliates}</div>
            <div className={styles.statLabel}>Total Affiliates</div>
          </div>
          <div className={`${styles.statCard} ${styles.activeCard}`}>
            <div className={styles.statNumber}>{activeAffiliates}</div>
            <div className={styles.statLabel}>Active</div>
          </div>
          <div className={`${styles.statCard} ${styles.deactivatedCard}`}>
            <div className={styles.statNumber}>{inactiveAffiliates}</div>
            <div className={styles.statLabel}>Inactive</div>
          </div>
          <div className={`${styles.statCard} ${styles.usersCard}`}>
            <div className={styles.statNumber}>{totalUsers}</div>
            <div className={styles.statLabel}>Total Users</div>
          </div>
          <div className={`${styles.statCard} ${styles.earnedCard}`}>
            <div className={styles.statNumber}>${totalEarned?.toFixed(2) || 0}</div>
            <div className={styles.statLabel}>Total Earned</div>
          </div>
          <div className={`${styles.statCard} ${styles.payoutsCard}`}>
            <div className={styles.statNumber}>${totalPayouts?.toFixed(2) || 0}</div>
            <div className={styles.statLabel}>Total Payouts</div>
          </div>
        </div>

        {/* FILTERS */}
        <div className={styles.filterPanel}>
          <input
            type="text"
            placeholder="Search affiliate..."
            className={styles.searchInput}
            onChange={(e) => setSearchText(`%${e.target.value}%`)}
          />
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <label style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Status:</label>
            <select
              className={styles.dropdownButton}
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as any)}
              style={{ minWidth: 120 }}
            >
              <option value="All">All Status</option>
              <option value="Active">Active</option>
              <option value="Deactive">Inactive</option>
            </select>
            <label style={{ fontSize: 13, color: 'var(--text-secondary)', marginLeft: 10 }}>Type:</label>
            <select
              className={styles.dropdownButton}
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value as any)}
              style={{ minWidth: 120 }}
            >
              <option value="All">All Types</option>
              <option value="Direct">Direct</option>
              <option value="Indirect">Indirect</option>
              <option value="Both">Both</option>
            </select>
          </div>
        </div>

        {/* CONTENT */}
        <div className={styles.content}>
          {/* LEFT PANEL */}
          <div className={styles.leftPanel}>
            <div className={styles.listHeader}>
              Affiliates (Showing {visibleAffiliateCount} of {effectiveAffiliateTotalCount || visibleAffiliateCount})
            </div>

            <div className={styles.listScroll}>
              {affiliates.map((a: any) => {
                const userCount = a.noOfUsers || 0;
                return (
                  <div
                    key={a.aId}
                    className={`${styles.card} ${selected?.aId === a.aId ? styles.selected : ""}`}
                    onClick={() => {
                      setSelected({
                        ...a,
                        aOrganization: a.aOrganization || "",
                        phoneNumber: a.phoneNumber || "",
                      });
                      setIsEditing(false);
                      setEditData({});
                      setUserPageOffset(0);
                      setPayoutPageOffset(0);
                      loadAffiliateDetails(a).then((enriched) => {
                        setSelected((current: typeof selected) =>
                          current?.aId === enriched?.aId ? enriched : current
                        );
                      });
                    }}
                  >
                    <div className={styles.cardHeader}>
                      <div>
                        <div className={styles.name}>{getAffiliateDisplayLabel(a)}</div>
                        <div className={styles.affId}>{a.referralCode}</div>
                        <div className={styles.org}>{a.aOrganization || ""}</div>
                      </div>
                      <span className={`${styles.badge} ${a.aStatus === "Active" ? styles.active : styles.inactive}`}>
                        {a.aStatus === "Deactive" ? "Inactive" : a.aStatus}
                      </span>
                    </div>
                    <div className={styles.bottomRow}>
                      <span className={`${styles.typeBadge} ${a.aType === "Direct" ? styles.directType : a.aType === "Indirect" ? styles.indirectType : styles.bothType}`}>
                        {a.aType}
                      </span>
                      <div className={styles.metric}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                          <path d="M16 3.128a4 4 0 0 1 0 7.744"></path>
                          <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                        </svg>
                        {userCount}
                      </div>
                      <div className={styles.metricAmount}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" x2="12" y1="2" y2="22"></line>
                          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                        </svg> ${a.totalAmount?.toFixed(2) || 0}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {affiliateTotalPages > 1 && (
              <div className={styles.pagination}>
                <button onClick={() => setAffiliatePageOffset(Math.max(0, affiliatePageOffset - limit))} disabled={affiliatePageOffset === 0}>
                  Previous
                </button>
                <span>Page {affiliateCurrentPage} of {affiliateTotalPages}</span>
                <button onClick={() => setAffiliatePageOffset(affiliatePageOffset + limit)} disabled={affiliatePageOffset + limit >= effectiveAffiliateTotalCount}>
                  Next
                </button>
              </div>
            )}
          </div>

          {/* RIGHT PANEL */}
          <div className={styles.rightPanel}>
            {!selected && (
              <div className={styles.emptyState}>
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                  <path d="M16 3.128a4 4 0 0 1 0 7.744"></path>
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                </svg>
                <h3>Select an Affiliate</h3>
                <p>Click an affiliate to view details</p>
              </div>
            )}

            {selected && (
              <>
                {/* TABS */}
                <div className={styles.tabs}>
                  <button className={activeTab === "details" ? styles.activeTab : styles.tab} onClick={() => setActiveTab("details")}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg> Details
                  </button>
                  <button className={activeTab === "users" ? styles.activeTab : styles.tab} onClick={() => setActiveTab("users")}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                      <path d="M16 3.128a4 4 0 0 1 0 7.744"></path>
                      <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                    </svg>
                    Users ({computedSelectedMetrics.noOfUsers})
                  </button>
                  <button className={activeTab === "payouts" ? styles.activeTab : styles.tab} onClick={() => setActiveTab("payouts")}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect width="20" height="14" x="2" y="5" rx="2"></rect>
                      <line x1="2" x2="22" y1="10" y2="10"></line>
                    </svg> Payouts
                  </button>
                </div>
                
                {/* HEADER with Actions - ONLY SHOW IN DETAILS TAB */}
                {activeTab === "details" && (
                  <div className={styles.detailsHeader}>
                    <div>
                      <h3>{getAffiliateDisplayLabel(selected)}</h3>
                      <p className={styles.affId}>{selected.referralCode}</p>
                    </div>
                    <div className={styles.actions}>
                      {!isEditing && canManageAffiliates && (
                        <>
                          <button className={styles.linkBtn} onClick={handleGenerateLinkForSelected} disabled={isGeneratingLink}>
                            {isGeneratingLink ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                              </svg>
                            )}{" "}
                            Generate Link
                          </button>
                          <button className={styles.primaryBtn} onClick={handleEditClick} disabled={isUpdatingAffiliate}>
                            {isUpdatingAffiliate ? <CircularProgress size={20} color="inherit" /> : "Edit"}
                          </button>
                        </>
                      )}
                      {isEditing && canManageAffiliates && (
                        <>
                          <button className={styles.cancelBtn} onClick={handleCancelClick} disabled={isUpdatingAffiliate}>
                            Cancel
                          </button>
                          <button className={styles.primaryBtn} onClick={handleSaveClick} disabled={isUpdatingAffiliate}>
                            {isUpdatingAffiliate ? <CircularProgress size={20} color="inherit" /> : "Save"}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* DETAILS TAB */}
                {activeTab === "details" && (
                  <>
                    <div className={styles.grid}>
                      <div className={styles.field}><label>First Name</label>{isEditing ? <input value={editData.firstName ?? selected.firstName ?? ''} onChange={(e) => setEditData({ ...editData, firstName: e.target.value })} disabled={isUpdatingAffiliate} /> : <p>{selected.firstName}</p>}</div>
                      <div className={styles.field}><label>Last Name</label>{isEditing ? <input value={editData.lastName ?? selected.lastName ?? ''} onChange={(e) => setEditData({ ...editData, lastName: e.target.value })} disabled={isUpdatingAffiliate} /> : <p>{selected.lastName}</p>}</div>
                      <div className={styles.field}><label>Organization</label>{isEditing ? <input value={editData.aOrganization ?? selected.aOrganization ?? ''} onChange={(e) => setEditData({ ...editData, aOrganization: e.target.value, isNonProfitOrganization: e.target.value.trim() ? editData.isNonProfitOrganization : false })} disabled={isUpdatingAffiliate} /> : <p>{selected.aOrganization || "-"}</p>}</div>
                      {(isEditing ? (editData.aOrganization ?? selected.aOrganization)?.trim() : selected.aOrganization?.trim()) && (
                        <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                          <label>Non-Profit Organization</label>
                          {isEditing ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                              <input type="checkbox" id="editIsNonProfitOrganization" checked={Boolean(editData.isNonProfitOrganization)} onChange={(e) => setEditData({ ...editData, isNonProfitOrganization: e.target.checked })} disabled={isUpdatingAffiliate} />
                              <label htmlFor="editIsNonProfitOrganization" style={{ margin: 0, fontSize: 14 }}>Is Non-Profit Organization</label>
                            </div>
                          ) : (
                            <p>{selected.isSupportVanderbiltNILFund ? 'Yes — users can donate tickets' : 'No — standard affiliate commission'}</p>
                          )}
                          {isEditing && editData.isNonProfitOrganization && <span style={{ fontSize: 12, color: '#64748b', marginTop: 6, display: 'block' }}>Users referred by this affiliate can choose to donate their ticket proceeds.</span>}
                        </div>
                      )}
                      <div className={styles.field}>
                        <label>Email</label>
                        {isEditing ? (
                          <>
                            <input type="email" value={editData.emailId ?? selected.emailId ?? ''} onChange={(e) => setEditData({ ...editData, emailId: e.target.value })} disabled={isUpdatingAffiliate} style={{ borderColor: ((editData.emailId !== undefined ? editData.emailId : selected.emailId) && !validateEditEmail(editData.emailId ?? selected.emailId)) ? '#ef4444' : undefined }} />
                            {((editData.emailId !== undefined ? editData.emailId : selected.emailId) && !validateEditEmail(editData.emailId ?? selected.emailId)) && <span style={{ color: '#ef4444', fontSize: 12, marginTop: 4, display: 'block' }}>Please enter a valid email address</span>}
                          </>
                        ) : <p>{selected.emailId}</p>}
                      </div>
                      <div className={styles.field}>
                        <label>Phone</label>
                        {isEditing ? (
                          <>
                            <input value={editData.phoneNumber ?? selected.phoneNumber ?? ''} onChange={(e) => { const formatted = formatPhoneNumber(e.target.value); setEditData({ ...editData, phoneNumber: formatted }); }} disabled={isUpdatingAffiliate} style={{ borderColor: (editData.phoneNumber && !validateEditPhoneNumber(editData.phoneNumber)) ? '#ef4444' : undefined }} />
                            {(editData.phoneNumber && !validateEditPhoneNumber(editData.phoneNumber)) && <span style={{ color: '#ef4444', fontSize: 12, marginTop: 4, display: 'block' }}>Please enter a valid phone number (minimum 10 digits)</span>}
                          </>
                        ) : <p>{selected.phoneNumber || "-"}</p>}
                      </div>
                      <div className={styles.field}><label>Status</label>{isEditing ? <select value={editData.aStatus ?? selected.aStatus ?? 'Active'} onChange={(e) => setEditData({ ...editData, aStatus: e.target.value })} disabled={isUpdatingAffiliate}><option value="Active">Active</option><option value="Deactive">Inactive</option></select> : <p>{selected.aStatus === "Deactive" ? "Inactive" : selected.aStatus}</p>}</div>
                      <div className={styles.field}><label>Type</label>{isEditing ? <select value={editData.aType ?? selected.aType ?? 'Direct'} onChange={(e) => setEditData({ ...editData, aType: e.target.value })} disabled={isUpdatingAffiliate}><option value="Direct">Direct</option><option value="Indirect">Indirect</option><option value="Both">Both</option></select> : <p>{selected.aType}</p>}</div>
                      <div className={styles.field}><label>Commission (%)</label><p>{selected.commission || 0}%</p></div>
                    </div>

                    <div className={styles.stats}>
                      <div className={styles.statBoxUsers}><div>{computedSelectedMetrics.noOfUsers}</div><span>Total Users</span></div>
                      <div className={styles.statBoxEarned}><div>${(computedSelectedMetrics.totalAmount || 0).toFixed(2)}</div><span>Total Earned</span></div>
                      <div className={styles.statBoxPayout}><div>${(computedSelectedMetrics.directPayout || 0).toFixed(2)}</div><span>Direct Payout</span></div>
                    </div>
                    <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
                      <div className={styles.field}><label>Sell Commission</label><p>${(computedSelectedMetrics.sellTotalAmount || 0).toFixed(2)}</p></div>
                      <div className={styles.field}><label>Buy Commission</label><p>${(computedSelectedMetrics.buyTotalAmount || 0).toFixed(2)}</p></div>
                      <div className={styles.field}><label>Signup Rewards</label><p>${(affiliateMetricsByKey.get(`a:${selected?.aId}`)?.signupReferralEarnings || 0).toFixed(2)}</p></div>
                      <div className={styles.field}><label>Indirect Payout</label><p>${(computedSelectedMetrics.indirectPayout || 0).toFixed(2)}</p></div>
                      <div className={styles.field}><label>Sell Direct</label><p>${(computedSelectedMetrics.sellDirectPayout || 0).toFixed(2)}</p></div>
                      <div className={styles.field}><label>Buy Direct</label><p>${(computedSelectedMetrics.buyDirectPayout || 0).toFixed(2)}</p></div>
                      <div className={styles.field}><label>Sell Indirect</label><p>${(computedSelectedMetrics.sellIndirectPayout || 0).toFixed(2)}</p></div>
                      <div className={styles.field}><label>Buy Indirect</label><p>${(computedSelectedMetrics.buyIndirectPayout || 0).toFixed(2)}</p></div>
                    </div>

                    <div className={styles.referral}>
                      <label>Affiliate Link</label>
                      {shortLinkError && <p role="alert">{shortLinkError}</p>}
                      <div className={styles.referralRow}>
                        <input readOnly value={shortLinkLoading ? 'Generating short link...' : getReferralLink()} style={{ cursor: 'pointer' }} title="Click to open link" onClick={() => { const url = getReferralLink(); if (url) window.open(url, '_blank', 'noopener,noreferrer'); }} />
                        <button disabled={!shortReferralLink} onClick={async () => { const link = getReferralLink(); const success = await copyTextToClipboard(link); if (success) { setCopied(true); toast.success("Link copied to clipboard!"); setTimeout(() => setCopied(false), 2000); } else { toast.error("Failed to copy link. Please copy manually."); } }}>{copied ? "Copied!" : "Copy"}</button>
                      </div>
                    </div>
                  </>
                )}

                {/* USERS TAB */}
                {activeTab === "users" && (
                  <div className={styles.tabContent}>
                    <div className={styles.usersTable}>
                      <h4>Users Under {getAffiliateDisplayLabel(selected)}</h4>
                      {(usersLoading || assignedUsersLoading) ? <div className={styles.loading}>Loading users...</div> : mergedAffiliateUsers.length > 0 ? (
                        <table className={styles.dataTable}>
                          <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Joined Date</th><th>Status</th></tr></thead>
                          <tbody>{mergedAffiliateUsers.map((user: any) => (<tr key={user.u_id}><td>{user.u_full_name || '-'}</td><td>{user.u_email_id || '-'}</td><td>{user.u_phone_number || '-'}</td><td>{user.u_created_at ? new Date(user.u_created_at).toLocaleDateString() : '-'}</td><td><span className={`${styles.statusBadge} ${user.u_is_active ? styles.active : styles.inactive}`}>{user.u_is_active ? 'Active' : 'Inactive'}</span></td></tr>))}</tbody>
                        </table>
                      ) : <p className={styles.noData}>No users assigned to this affiliate</p>}
                    </div>
                  </div>
                )}

                {/* PAYOUTS TAB */}
                {activeTab === "payouts" && (
                  <div className={styles.tabContent}>
                    <div className={styles.payoutSection}>
                      <h4>Payout History for {getAffiliateDisplayLabel(selected)}</h4>
                      {payoutsLoading ? <div className={styles.loading}>Loading payouts...</div> : affiliatePayouts.length > 0 ? (
                        <>
                          <table className={styles.dataTable}>
                            <thead><tr><th>Payout ID</th><th>Amount</th><th>Status</th><th>Date</th><th>Method</th></tr></thead>
                            <tbody>{affiliatePayouts.map((payout: any, index: number) => { const payoutId = payout?.payoutId || payout?.payout_id || payout?.id || `payout-${index}`; const amount = Number(payout?.amount || payout?.payout_amount || 0); const status = payout?.status || payout?.payout_status || "Pending"; const createdAt = payout?.createdAt || payout?.created_at || payout?.date; const paymentMethod = payout?.paymentMethod || payout?.payment_method || "Bank Transfer"; return (<tr key={payoutId}><td>{String(payoutId).substring(0, 8)}...</td><td>${amount.toFixed(2)}</td><td><span className={`${styles.statusBadge} ${String(status).toLowerCase() === 'completed' ? styles.success : String(status).toLowerCase() === 'pending' ? styles.warning : styles.error}`}>{status}</span></td><td>{createdAt ? new Date(createdAt).toLocaleDateString() : '-'}</td><td>{paymentMethod}</td></tr>); })}</tbody>
                          </table>
                          {payoutTotalPages > 1 && (<div className={styles.pagination}><button onClick={() => setPayoutPageOffset(Math.max(0, payoutPageOffset - pageSize))} disabled={payoutPageOffset === 0}>Previous</button><span>Page {payoutCurrentPage} of {payoutTotalPages}</span><button onClick={() => setPayoutPageOffset(payoutPageOffset + pageSize)} disabled={payoutPageOffset + pageSize >= payoutTotalCount}>Next</button></div>)}
                        </>
                      ) : <p className={styles.noData}>No payout history found for this affiliate</p>}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ADD AFFILIATE MODAL */}
      {showAddAffiliate && (
        <div className={styles.popupOverlay}>
          <div className={styles.popupCard} style={{ maxWidth: 540, maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden', boxShadow: isDarkMode ? '0 10px 40px rgba(0,0,0,0.5)' : '0 10px 40px rgba(0,0,0,0.18)' }}>
            <div style={{ padding: '24px 32px', borderBottom: `1px solid ${isDarkMode ? '#334155' : '#e5e7eb'}`, background: isDarkMode ? '#1e293b' : '#fff', flexShrink: 0, transition: 'all 0.3s ease' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 20, color: isDarkMode ? '#f1f5f9' : '#222', transition: 'color 0.3s ease' }}>
                  <svg width="22" height="22" fill="none" stroke={isDarkMode ? '#818cf8' : '#2563eb'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="12" cy="7" r="4"/><path d="M5.5 21a7.5 7.5 0 0 1 13 0"/></svg>
                  Add New Affiliate
                </div>
                <button className={styles.closeBtn} style={{ fontSize: 28, lineHeight: 1, padding: 0, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', color: isDarkMode ? '#f1f5f9' : '#222', transition: 'color 0.3s ease' }} onClick={() => { setShowAddAffiliate(false); setValidationErrors({}); setReferredAffiliateSearch(""); setNewAffiliateData({ ...newAffiliateData, referredByItem: null, referredBy: null }); }} disabled={isAddingAffiliate}>&times;</button>
              </div>
              <div style={{ marginTop: 6, color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: 14, transition: 'color 0.3s ease' }}>Fill in the details below to create a new affiliate. Affiliates are not given access to this admin dashboard.</div>
            </div>
            <form onSubmit={handleAddAffiliate} style={{ flex: 1, overflowY: 'auto', padding: '24px 32px', background: isDarkMode ? '#1e293b' : '#fff', transition: 'all 0.3s ease' }}>
              <div style={{ display: 'flex', gap: 18 }}>
                <div className={styles.field} style={{ flex: 1 }}><label style={{ color: isDarkMode ? '#f1f5f9' : undefined }}>First Name *</label><input type="text" required placeholder="First name" value={newAffiliateData.firstName} onChange={(e) => { setNewAffiliateData({ ...newAffiliateData, firstName: e.target.value }); if (validationErrors.firstName) setValidationErrors({ ...validationErrors, firstName: undefined }); }} style={{ borderColor: validationErrors.firstName ? '#ef4444' : undefined, backgroundColor: isDarkMode ? '#334155' : '#fff', color: isDarkMode ? '#f1f5f9' : '#111827' }} disabled={isAddingAffiliate} />{validationErrors.firstName && <span style={{ color: '#ef4444', fontSize: 12, marginTop: 4, display: 'block' }}>{validationErrors.firstName}</span>}</div>
                <div className={styles.field} style={{ flex: 1 }}><label style={{ color: isDarkMode ? '#f1f5f9' : undefined }}>Last Name *</label><input type="text" required placeholder="Last name" value={newAffiliateData.lastName} onChange={(e) => { setNewAffiliateData({ ...newAffiliateData, lastName: e.target.value }); if (validationErrors.lastName) setValidationErrors({ ...validationErrors, lastName: undefined }); }} style={{ borderColor: validationErrors.lastName ? '#ef4444' : undefined, backgroundColor: isDarkMode ? '#334155' : '#fff', color: isDarkMode ? '#f1f5f9' : '#111827' }} disabled={isAddingAffiliate} />{validationErrors.lastName && <span style={{ color: '#ef4444', fontSize: 12, marginTop: 4, display: 'block' }}>{validationErrors.lastName}</span>}</div>
              </div>
              <div className={styles.field} style={{ marginTop: 16 }}><label style={{ color: isDarkMode ? '#f1f5f9' : undefined }}>Email *</label><input type="email" required placeholder="email@example.com" value={newAffiliateData.emailId} onChange={(e) => { setNewAffiliateData({ ...newAffiliateData, emailId: e.target.value }); if (validationErrors.emailId) setValidationErrors({ ...validationErrors, emailId: undefined }); }} style={{ borderColor: validationErrors.emailId ? '#ef4444' : undefined, backgroundColor: isDarkMode ? '#334155' : '#fff', color: isDarkMode ? '#f1f5f9' : '#111827' }} disabled={isAddingAffiliate} />{validationErrors.emailId && <span style={{ color: '#ef4444', fontSize: 12, marginTop: 4, display: 'block' }}>{validationErrors.emailId}</span>}</div>
              <div className={styles.field} style={{ marginTop: 16 }}><label style={{ color: isDarkMode ? '#f1f5f9' : undefined }}>Phone Number *</label><input type="tel" required placeholder="(555) 123-4567" value={newAffiliateData.phoneNumber} onChange={(e) => { const formatted = formatPhoneNumber(e.target.value); setNewAffiliateData({ ...newAffiliateData, phoneNumber: formatted }); if (validationErrors.phoneNumber) setValidationErrors({ ...validationErrors, phoneNumber: undefined }); }} style={{ borderColor: validationErrors.phoneNumber ? '#ef4444' : undefined, backgroundColor: isDarkMode ? '#334155' : '#fff', color: isDarkMode ? '#f1f5f9' : '#111827' }} disabled={isAddingAffiliate} />{validationErrors.phoneNumber && <span style={{ color: '#ef4444', fontSize: 12, marginTop: 4, display: 'block' }}>{validationErrors.phoneNumber}</span>}<span style={{ fontSize: 12, color: isDarkMode ? '#94a3b8' : '#64748b', marginTop: 4, display: 'block' }}>Enter a valid phone number with at least 10 digits</span></div>
              <div className={styles.field} style={{ marginTop: 16 }}><label style={{ color: isDarkMode ? '#f1f5f9' : undefined }}>Organization (Optional)</label><input type="text" placeholder="Company or organization name" value={newAffiliateData.organization} onChange={(e) => { const organization = e.target.value; setNewAffiliateData({ ...newAffiliateData, organization, isNonProfitOrganization: organization.trim() ? newAffiliateData.isNonProfitOrganization : false }); }} style={{ backgroundColor: isDarkMode ? '#334155' : '#fff', color: isDarkMode ? '#f1f5f9' : '#111827' }} disabled={isAddingAffiliate} /></div>
              {newAffiliateData.organization?.trim() && (<div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 16 }}><div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><input type="checkbox" id="isNonProfitOrganization" style={{ margin: 0, accentColor: isDarkMode ? '#818cf8' : '#2563eb' }} checked={newAffiliateData.isNonProfitOrganization} onChange={(e) => setNewAffiliateData({ ...newAffiliateData, isNonProfitOrganization: e.target.checked })} disabled={isAddingAffiliate} /><label htmlFor="isNonProfitOrganization" style={{ margin: 0, fontSize: 14, color: isDarkMode ? '#f1f5f9' : '#222' }}>Is Non-Profit Organization</label></div><span style={{ fontSize: 12, color: isDarkMode ? '#94a3b8' : '#64748b', paddingLeft: 26 }}>{newAffiliateData.isNonProfitOrganization ? 'Users referred by this affiliate can choose to donate their ticket proceeds to the organization.' : 'Leave unchecked for standard affiliate commission on ticket sales.'}</span></div>)}
              <div className={styles.field} style={{ marginTop: 16 }}><label style={{ color: isDarkMode ? '#f1f5f9' : undefined }}>Affiliate Type *</label><select value={newAffiliateData.aType} onChange={(e) => setNewAffiliateData({ ...newAffiliateData, aType: e.target.value })} style={{ backgroundColor: isDarkMode ? '#334155' : '#fff', color: isDarkMode ? '#f1f5f9' : '#111827', borderColor: isDarkMode ? '#475569' : '#e5e7eb' }} disabled={isAddingAffiliate}><option value="Direct">Direct</option><option value="Indirect">Indirect</option><option value="Both">Both</option></select></div>
              {(newAffiliateData.aType === "Direct" || newAffiliateData.aType === "Both") && (
                <div style={{ marginTop: 18, background: isDarkMode ? '#334155' : '#f6f8fa', borderRadius: 10, padding: 18, border: `1px solid ${isDarkMode ? '#475569' : '#e5e7eb'}`, transition: 'all 0.3s ease' }}>
                  <div style={{ fontWeight: 600, color: isDarkMode ? '#818cf8' : '#2563eb', marginBottom: 10, fontSize: 15, display: 'flex', alignItems: 'center', gap: 8, transition: 'color 0.3s ease' }}><svg width="18" height="18" fill="none" stroke={isDarkMode ? '#818cf8' : '#2563eb'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="12" cy="7" r="4"/><path d="M5.5 21a7.5 7.5 0 0 1 13 0"/></svg>Direct Affiliate Settings</div>
                  <div className={styles.field} style={{ marginBottom: 12 }}>
                    <label style={{ color: isDarkMode ? '#f1f5f9' : undefined }}>Referred Affiliate (Optional)</label>
                    <AffiliatesAndSalesRepsDropdown
                      affiliates={affiliates}
                      salesReps={salesRepsList}
                      selectedItem={newAffiliateData.referredByItem || null}
                      onSelect={(item) => {
                        if (!item) {
                          setNewAffiliateData({ ...newAffiliateData, referredBy: null, referredByItem: null });
                        } else if (item.type === "affiliate") {
                          setNewAffiliateData({ ...newAffiliateData, referredBy: (item as any).aId, referredByItem: item });
                        } else {
                          // For sales reps, store the sales rep ID
                          setNewAffiliateData({ ...newAffiliateData, referredBy: (item as any).sr_id, referredByItem: item });
                        }
                      }}
                      isDarkMode={isDarkMode}
                      placeholder="Search by name, email or ID"
                    />
                    <span style={{ fontSize: 12, color: isDarkMode ? '#94a3b8' : '#64748b', marginTop: 8 }}>
                      Search and select an existing affiliate or sales rep for this direct referral.
                    </span>
                  </div>
                  <div className={styles.field} style={{ marginBottom: 12 }}><label style={{ color: isDarkMode ? '#f1f5f9' : undefined }}>URL Suffix (Optional)</label><div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="text" placeholder="e.g. john-sports" value={newAffiliateData.urlSuffix} onChange={(e) => { setNewAffiliateData({ ...newAffiliateData, urlSuffix: e.target.value }); if (validationErrors.urlSuffix) setValidationErrors({ ...validationErrors, urlSuffix: undefined }); }} style={{ background: isDarkMode ? '#1e293b' : '#fff', flex: 1, borderColor: validationErrors.urlSuffix ? '#ef4444' : isDarkMode ? '#475569' : '#e5e7eb', color: isDarkMode ? '#f1f5f9' : '#111827' }} disabled={isAddingAffiliate} /><button type="button" className={styles.primaryBtn} style={{ padding: '7px 14px', fontSize: 13, borderRadius: 6, fontWeight: 500, whiteSpace: 'nowrap', background: isDarkMode ? '#818cf8' : '#2563eb', color: '#fff' }} onClick={generateReferralLink} disabled={isAddingAffiliate || isGeneratingLink}>{isGeneratingLink ? <CircularProgress size={16} color="inherit" /> : "Generate Link"}</button></div>{validationErrors.urlSuffix && <span style={{ color: '#ef4444', fontSize: 12, marginTop: 4, display: 'block' }}>{validationErrors.urlSuffix}</span>}<span style={{ fontSize: 12, color: isDarkMode ? '#94a3b8' : '#64748b' }}>Custom URL for the affiliate's referral link (letters, numbers, hyphens only)</span>{(generatedLink || newAffiliateData.referralLink) && <div style={{ marginTop: 8, fontSize: 13, color: isDarkMode ? '#93c5fd' : '#2563eb', wordBreak: 'break-all' }}><b>Preview Link:</b> {generatedLink || newAffiliateData.referralLink}</div>}</div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, padding: '20px 32px', borderTop: `1px solid ${isDarkMode ? '#334155' : '#e5e7eb'}`, background: isDarkMode ? '#1e293b' : '#fff', flexShrink: 0, justifyContent: 'flex-end' }}>
                <button type="button" className={styles.cancelBtn} style={{ fontWeight: 500, fontSize: 14, padding: '10px 20px', borderRadius: 8, border: `1px solid ${isDarkMode ? '#475569' : '#e5e7eb'}`, background: isDarkMode ? '#334155' : '#fff', color: isDarkMode ? '#f1f5f9' : '#374151', cursor: 'pointer' }} onClick={() => { setShowAddAffiliate(false); setValidationErrors({}); setReferredAffiliateSearch(""); setNewAffiliateData({ ...newAffiliateData, referredByItem: null, referredBy: null }); }} disabled={isAddingAffiliate}>Cancel</button>
                <button type="submit" className={styles.primaryBtn} style={{ background: isDarkMode ? '#818cf8' : '#2563eb', color: '#fff', fontWeight: 500, fontSize: 14, borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8, border: 'none', cursor: isAddingAffiliate ? 'not-allowed' : 'pointer', opacity: isAddingAffiliate ? 0.7 : 1 }} disabled={isAddingAffiliate}>{isAddingAffiliate ? (<><CircularProgress size={18} color="inherit" /> Creating...</>) : "+ Create Affiliate"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* REFERRAL LINK POPUP */}
      {showReferralPopup && selected && (
        <div className={styles.popupOverlay}>
          <div className={styles.popupCard}>
            <div className={styles.popupHeader}>
              <div className={styles.popupTitle}><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>Affiliate Link Generated</div>
              <button className={styles.closeBtn} onClick={() => setShowReferralPopup(false)}>✕</button>
            </div>
            <p className={styles.popupDesc} style={{ color: isDarkMode ? '#f1f5f9' : undefined }}>Share this link with the affiliate to track their referrals:</p>
            {shortLinkError && <p role="alert">{shortLinkError}</p>}
            <div className={styles.linkRow}><input readOnly value={shortLinkLoading ? 'Generating short link...' : getReferralLink()} style={{ backgroundColor: isDarkMode ? '#334155' : '#f8fafc', color: isDarkMode ? '#f1f5f9' : '#111827', borderColor: isDarkMode ? '#475569' : '#e5e7eb' }} /><button disabled={!shortReferralLink} className={styles.copyBtn} style={{ backgroundColor: isDarkMode ? '#818cf8' : '#2563eb' }} onClick={async () => { const success = await copyTextToClipboard(getReferralLink()); if (success) { setCopied(true); toast.success("Link copied to clipboard!"); setTimeout(() => setCopied(false), 2000); } else { toast.error("Failed to copy link. Please copy manually."); } }}>{copied ? "Copied!" : "Copy"}</button></div>
            <div className={styles.popupFooter}><button className={styles.closePopupBtn} style={{ backgroundColor: isDarkMode ? '#818cf8' : '#2563eb' }} onClick={() => setShowReferralPopup(false)}>Close</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
